import React, { useEffect, useState } from "react";
import ReactApexChart from "react-apexcharts";
import { getFormatedDate } from "../../../../helper";
import { makeStyles } from "@material-ui/core/styles";

const useStyles = makeStyles((_theme) => ({
  chartContainer: {
    position: "relative",
    overflow: "hidden",
  },
  sliderContainer: {
    margin: "20px 0",
    padding: "0 30px",
    position: "relative",
    maxWidth: "600px",
    marginLeft: "auto",
    marginRight: "auto",
  },
  sliderWrapper: {
    position: "relative",
    height: "8px",
    display: "flex",
    alignItems: "center",
  },
  slider: {
    WebkitAppearance: "none",
    width: "100%",
    height: "4px",
    background: "#e0e0e0",
    borderRadius: "2px",
    outline: "none",
    "&::-webkit-slider-thumb": {
      WebkitAppearance: "none",
      width: "20px",
      height: "20px",
      background: "#0067A1",
      borderRadius: "50%",
      cursor: "pointer",
      transition: "all 0.2s ease",
      boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
      "&:hover": {
        transform: "scale(1.1)",
        background: "#004d80",
      },
      "&:active": {
        transform: "scale(1)",
      },
    },
  },
  pageIndicator: {
    position: "absolute",
    right: "0",
    bottom: "-24px",
    color: "#666",
    fontSize: "0.8rem",
    fontWeight: 400,
    fontFamily: "system-ui, sans-serif",
    "& span": {
      color: "#0067A1",
      fontWeight: 500,
    },
  },
  progressTrack: {
    position: "absolute",
    height: "4px",
    background: "#0067A1",
    borderRadius: "2px",
    transition: "width 0.3s ease",
  },
}));

const AccuracyChart = ({ _activeTab, graphData = [], _type }) => {
  const classes = useStyles();
  const [borderRadius, setBorderRadius] = useState(10);
  const [currentPage, setCurrentPage] = useState(0);
  const itemsPerPage = 5;

  useEffect(() => {
    const handleResize = () => {
      const screenWidth = window.innerWidth;
      if (screenWidth < 1000) {
        setBorderRadius(2);
      } else if (screenWidth < 1500) {
        setBorderRadius(5);
      } else {
        setBorderRadius(7);
      }
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const totalItems = graphData.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startIndex = currentPage * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;

  // Determine the categories, series, and yAxisTitle dynamically based on activeTab
  let categories = [];
  let seriesData = [];

  categories = graphData?.map((item) => item?.Entity || "Unknown Metric");
  seriesData = [
    {
      name: "# of Orders",
      type: "bar",
      data: graphData?.map((item) => item["# of Orders"] || 0),
    },
    {
      name: "% of Orders",
      type: "line",
      data: graphData?.map((item) => item["% of Orders"] || 0),
    },
    // {
    //     name: "Threshold",
    //     type: "line",
    //     data: graphData?.map((item) => item["Threshold"] || 0),
    // },
  ];

  // Slice data for current page
  const slicedCategories = categories?.slice(startIndex, endIndex);
  const slicedSeries = seriesData?.map((s) => ({
    ...s,
    data: s.data.slice(startIndex, endIndex),
  }));

  const options = {
    chart: {
      height: 350,
      type: "bar",
      stacked: false,
      toolbar: {
        show: false,
      },
    },
    plotOptions: {
      bar: {
        horizontal: false,
        columnWidth: `${(graphData?.length / 3) * 10}%`,
        borderRadius: borderRadius,
        borderRadiusApplication: "end",
        borderRadiusWhenStacked: "last",
      },
    },
    grid: {
      show: false,
    },
    dataLabels: {
      enabled: false,
    },
    stroke: {
      width: [3, 3, 3], // thickness for each series
      curve: "straight",
      dashArray: [0, 0, 10], // 5 = dotted line for the third series ("Threshold")
    },
    series: seriesData,
    colors: ["#EC6FBD", "#A4D65D", "#808080", "#F7D240"],
    xaxis: {
      categories: slicedCategories,
      labels: {
        maxHeight: 100,
        rotate: -45,
        show: true,
        trim: true,
        hideOverlappingLabels: false,
        showDuplicates: true,
        rotateAlways: true,
        cssClass: "force-xaxis-rotate",
        style: {
          fontSize: "12px",
          fontFamily: "Poppins, sans-serif",
          transform: "rotate(-45deg) !important",
          transformOrigin: "top right !important",
          whiteSpace: "pre-line",
        },
      },
    },
    yaxis: [
      {
        seriesName: ["# of Orders"],
        title: {
          text: "# of Orders",
          style: {
            fontSize: "12px",
            fontFamily: "Poppins, sans-serif",
            fontWeight: 500,
            color: "#333",
          },
        },
        labels: {
          formatter: (value) => value,
        },
      },
      {
        // Right y-axis for accuracy (percentage)
        opposite: true, // Place the axis on the right side
        seriesName: ["% of Orders", "Threshold"],
        title: {
          text: "% of Orders",
          style: {
            fontSize: "12px",
            fontFamily: "Poppins, sans-serif",
            fontWeight: 500,
            color: "#333",
          },
        },
        labels: {
          formatter: (value) => `${value.toFixed(0)}%`,
        },
      },
    ],
    tooltip: {
      shared: true,
      intersect: false,
      y: [
        {
          formatter: (value) => value,
        },
        {
          formatter: (value) => `${value.toFixed(2)}%`,
        },
        {
          formatter: (value) => value,
        },
      ],
    },
    legend: {
      position: "top",
      horizontalAlign: "left",
      markers: {
        width: 12,
        height: 12,
        radius: 10,
        shape: "circle",
      },
    },
  };

  return (
    <div className="grouped-bar-line-chart">
      <ReactApexChart
        options={options}
        series={slicedSeries}
        type="bar"
        height={350}
      />
      {totalPages > 1 && (
        <div className={classes.sliderContainer}>
          <div className={classes.sliderWrapper}>
            <div
              className={classes.progressTrack}
              style={{
                width: `${((currentPage + 1) / totalPages) * 100}%`,
                opacity: 0.3,
              }}
            />
            <input
              type="range"
              min="0"
              max={totalPages - 1}
              value={currentPage}
              onChange={(e) => setCurrentPage(parseInt(e.target.value, 10))}
              className={classes.slider}
            />
          </div>
          <div className={classes.pageIndicator}>
            <span>{currentPage + 1}</span> / {totalPages}
          </div>
        </div>
      )}
    </div>
  );
};

export default AccuracyChart;
